/**
 * The editor inside PRISM (ADR 0006, 0024): when the editor runs in a frame, it connects the bridge with the PRISM
 * origins from /api/v1/config, waits for host:init, and keeps the editor token in memory only (api.setToken; it is
 * sent as a bearer and never put in a URL, a cookie, storage or a log). host:token replaces it, host:theme switches
 * the theme. When the token runs out without a refresh, the session is "expired": the editor becomes read-only.
 *
 * The request-save and request-close handlers belong to the open editor (prism/SaveFlow.tsx); until one is
 * registered, the bridge answers NOT_READY (save) or closes (nothing can be unsaved).
 */
import { useSyncExternalStore } from 'react';
import { connectEditor, type EditorBridge, type HostInit, type SavedPayload, type Theme } from '@prism-graphics/bridge/editor';
import { api, setToken, type ClientConfig, type Me } from '../api.ts';
import { setLocale } from '../i18n.ts';

export type PrismStatus = 'standalone' | 'waiting' | 'ready' | 'expired';

export interface PrismSessionState {
  status: PrismStatus;
  init?: HostInit;
  me?: Me;
  theme?: Theme;
  /** When the current token expires (ms since the epoch). */
  expiresAt?: number;
}

type RequestHandler = (requestId: string) => void | Promise<void>;

class PrismSession {
  state: PrismSessionState = { status: 'standalone' };
  bridge: EditorBridge | undefined;
  #listeners = new Set<() => void>();
  #expiry: ReturnType<typeof setTimeout> | undefined;
  #save: RequestHandler | undefined;
  #close: RequestHandler | undefined;

  subscribe = (fn: () => void) => {
    this.#listeners.add(fn);
    return () => void this.#listeners.delete(fn);
  };

  getState = () => this.state;

  #set(patch: Partial<PrismSessionState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.#listeners) l();
  }

  /** In a frame: connect the bridge (once). Returns whether the editor is embedded. */
  start(config: ClientConfig): boolean {
    if (this.bridge) return true;
    if (window.parent === window) return false;
    const bridge = connectEditor({
      hostOrigins: config.prismOrigins,
      versions: [1],
      logger: { warn: (m) => console.warn(m), error: (m) => console.error(m) },
    });
    this.bridge = bridge;
    this.#set({ status: 'waiting' });
    bridge.onInit((init) => {
      this.#token(init.token.value, init.token.expiresAt);
      applyTheme(init.theme);
      setLocale(init.user.locale || 'en');
      this.#set({ init, theme: init.theme });
      // The API's view of this user (permissions from PRISM /me).
      void api.me().then(
        (me) => this.#set({ me, status: this.state.status === 'expired' ? 'expired' : 'ready' }),
        () => this.#set({ status: this.state.status === 'expired' ? 'expired' : 'ready' }),
      );
    });
    bridge.onToken((t) => this.#token(t.value, t.expiresAt));
    bridge.onTheme((theme) => {
      applyTheme(theme);
      this.#set({ theme });
    });
    bridge.onRequestSave((id) => {
      if (this.#save) return this.#save(id);
      bridge.error({ code: 'NOT_READY', message: 'The editor has no graphic open yet' }, id);
    });
    bridge.onRequestClose((id) => {
      if (this.#close) return this.#close(id);
      bridge.close({ saved: false }, id);
    });
    return true;
  }

  #token(value: string, expiresAt: string): void {
    setToken(value);
    const at = Date.parse(expiresAt);
    if (this.#expiry) clearTimeout(this.#expiry);
    const ms = at - Date.now();
    this.#set({ expiresAt: at, ...(this.state.status === 'expired' ? { status: 'ready' as const } : {}) });
    // Past expiry without a new token: read-only "session expired".
    this.#expiry = setTimeout(() => this.expire(), Math.max(0, Math.min(ms, 2 ** 31 - 1)));
  }

  /** The token ran out (or PRISM refused it). */
  expire(): void {
    if (this.state.status === 'standalone' || this.state.status === 'expired') return;
    setToken(null);
    this.#set({ status: 'expired' });
    this.bridge?.error({ code: 'TOKEN_EXPIRED', message: 'The editor session expired; reopen the editor from PRISM' });
  }

  get embedded(): boolean {
    return this.state.status !== 'standalone';
  }

  can(p: Me['permissions'][number]): boolean {
    if (this.state.status === 'standalone') return true;
    return this.state.status === 'ready' && (this.state.me?.permissions.includes(p) ?? false);
  }

  /** The open editor answers host:request-save and host:request-close. */
  handle(handlers: { save: RequestHandler; close: RequestHandler }): () => void {
    this.#save = handlers.save;
    this.#close = handlers.close;
    return () => {
      if (this.#save === handlers.save) this.#save = undefined;
      if (this.#close === handlers.close) this.#close = undefined;
    };
  }

  dirty(d: boolean): void {
    this.bridge?.dirty(d);
  }

  saved(s: SavedPayload, requestId?: string): void {
    this.bridge?.saved(s, requestId);
  }
}

export const prism = new PrismSession();
(window as unknown as { __pgPrism?: PrismSession }).__pgPrism = prism;

export function usePrism<T>(select: (s: PrismSessionState) => T): T {
  return useSyncExternalStore(prism.subscribe, () => select(prism.state));
}

/** PRISM's theme for this session (not remembered: standalone keeps its own choice). */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
}
